import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  ImageIcon, 
  Package,
  Coffee,
  Frame,
  Check,
  RefreshCw,
  Sparkles
} from 'lucide-react';

// ========================
// PRODUCTS
// ========================
const PRODUCTS = [
  { id: 'sticker', name: 'Sticker', icon: Package },
  { id: 'mug', name: 'Mug', icon: Coffee },
  { id: 'wall_art', name: 'Wall Art', icon: Frame },
];

// ========================
// TEMPLATES
// ========================
const TEMPLATES = {
  sticker: [
    { id: 'handheld', name: 'Handheld', file: '/templates/sticker/handheld.png' },
    { id: 'laptop', name: 'Laptop', file: '/templates/sticker/laptop.png' },
    { id: 'water_bottle', name: 'Water Bottle', file: '/templates/sticker/water_bottle.png' },
    { id: 'tabletop', name: 'Tabletop', file: '/templates/sticker/tabletop.png' },
    { id: 'notebook', name: 'Notebook', file: '/templates/sticker/notebook.png' },
  ],
  mug: [
    { id: 'clean_studio', name: 'Studio', file: '/templates/mug/clean_studio.png' },
    { id: 'cozy_blanket', name: 'Cozy', file: '/templates/mug/cozy_blanket.png' },
    { id: 'desk', name: 'Desk', file: '/templates/mug/desk.png' },
    { id: 'kitchen', name: 'Kitchen', file: '/templates/mug/kitchen.png' },
    { id: 'outdoor', name: 'Outdoor', file: '/templates/mug/outdoor.png' },
  ],
  wall_art: [
    { id: 'minimal_living_room', name: 'Living Room', file: '/templates/wall_art/minimal_living_room.png' },
    { id: 'bedroom', name: 'Bedroom', file: '/templates/wall_art/bedroom.png' },
    { id: 'nursery', name: 'Nursery', file: '/templates/wall_art/nursery.png' },
    { id: 'office', name: 'Office', file: '/templates/wall_art/office.png' },
    { id: 'gallery_wall', name: 'Gallery', file: '/templates/wall_art/gallery_wall.png' },
  ],
};

// ========================
// PLACEMENTS
// ========================
const PLACEMENTS = {
  sticker: [
    { id: 'center', name: 'Center', x: 50, y: 50, scale: 35 },
    { id: 'top_left', name: 'Top Left', x: 35, y: 38, scale: 30 },
    { id: 'bottom_right', name: 'Bottom Right', x: 65, y: 62, scale: 30 },
  ],
  mug: [
    { id: 'center', name: 'Center', x: 50, y: 52, scale: 24 },
    { id: 'left', name: 'Left', x: 44, y: 52, scale: 22 },
    { id: 'right', name: 'Right', x: 56, y: 52, scale: 22 },
  ],
  wall_art: [
    { id: 'fit', name: 'Fit', x: 50, y: 48, scale: 50 },
    { id: 'fill', name: 'Fill', x: 50, y: 48, scale: 58 },
    { id: 'margin', name: 'Margin', x: 50, y: 48, scale: 42 },
  ],
};

// Design bounds per product
const BOUNDS = {
  sticker: { x: 15, y: 15, w: 70, h: 70 },
  mug: { x: 36, y: 30, w: 28, h: 44 },
  wall_art: { x: 22, y: 17, w: 56, h: 66 },
};

const MockupGenerator = () => {
  const { api } = useAuth();
  
  // State
  const [designImage, setDesignImage] = useState(null);
  const [designPreview, setDesignPreview] = useState(null);
  const [product, setProduct] = useState('');
  const [template, setTemplate] = useState('');
  const [placement, setPlacement] = useState('');
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Refs
  const canvasRef = useRef(null);
  const bgImageRef = useRef(null);
  const designRef = useRef(null);
  const fileInputRef = useRef(null);

  // ========================
  // FILE UPLOAD
  // ========================
  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Max file size is 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setDesignPreview(dataUrl);
      setDesignImage(dataUrl);
      setResult(null);
      
      const img = new Image();
      img.onload = () => { designRef.current = img; };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  // ========================
  // SELECTIONS
  // ========================
  const handleProductSelect = (id) => {
    setProduct(id);
    setTemplate('');
    setPlacement('');
    setResult(null);
  };

  const handleTemplateSelect = (id) => {
    setTemplate(id);
    setPlacement('');
    setResult(null);
    
    const tpl = TEMPLATES[product]?.find(t => t.id === id);
    if (tpl) {
      const img = new Image();
      img.onload = () => { bgImageRef.current = img; };
      img.src = tpl.file;
    }
  };

  const handlePlacementSelect = (id) => {
    setPlacement(id);
    setResult(null);
  };

  // ========================
  // GENERATE
  // ========================
  const handleGenerate = () => {
    if (!designImage || !product || !template || !placement) {
      toast.error('Complete all selections first');
      return;
    }
    if (!bgImageRef.current || !designRef.current) {
      toast.error('Images not loaded. Try again.');
      return;
    }

    setGenerating(true);
    
    setTimeout(() => {
      try {
        renderMockup();
        setGenerating(false);
      } catch (err) {
        console.error('Render error:', err);
        toast.error('Failed to generate');
        setGenerating(false);
      }
    }, 100);
  };

  const renderMockup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const bg = bgImageRef.current;
    const design = designRef.current;
    
    canvas.width = bg.width;
    canvas.height = bg.height;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bg, 0, 0);
    
    const bounds = BOUNDS[product];
    const place = PLACEMENTS[product]?.find(p => p.id === placement);
    if (!bounds || !place) return;
    
    const boundsX = canvas.width * bounds.x / 100;
    const boundsY = canvas.height * bounds.y / 100;
    const boundsW = canvas.width * bounds.w / 100;
    const boundsH = canvas.height * bounds.h / 100;
    
    const maxSize = Math.min(boundsW, boundsH);
    const aspectRatio = design.width / design.height;
    let drawW = maxSize * (place.scale / 100);
    let drawH = drawW / aspectRatio;
    
    if (drawH > maxSize * (place.scale / 100)) {
      drawH = maxSize * (place.scale / 100);
      drawW = drawH * aspectRatio;
    }
    
    const centerX = boundsX + (boundsW * place.x / 100);
    const centerY = boundsY + (boundsH * place.y / 100);
    
    ctx.save();
    
    if (product === 'wall_art') {
      ctx.beginPath();
      ctx.rect(boundsX, boundsY, boundsW, boundsH);
      ctx.clip();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(boundsX, boundsY, boundsW, boundsH);
    }
    
    if (product === 'sticker') {
      ctx.shadowColor = 'rgba(0,0,0,0.2)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 5;
    } else if (product === 'mug') {
      ctx.shadowColor = 'rgba(0,0,0,0.12)';
      ctx.shadowBlur = 5;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
    }
    
    ctx.drawImage(design, centerX - drawW/2, centerY - drawH/2, drawW, drawH);
    ctx.restore();
    
    setResult(canvas.toDataURL('image/png'));
  };

  // ========================
  // DOWNLOAD
  // ========================
  const handleDownload = async () => {
    if (!result) return;
    
    try {
      const base64 = result.split(',')[1];
      await api.post('/ai/save-etsy-mockup', {
        image_base64: base64,
        product_type: product,
        preset: template
      });
    } catch (err) {
      console.error('Save error:', err);
    }
    
    const link = document.createElement('a');
    link.href = result;
    link.download = `mockup-${product}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Downloaded!');
  };

  // ========================
  // RESET
  // ========================
  const handleReset = () => {
    setDesignImage(null);
    setDesignPreview(null);
    setProduct('');
    setTemplate('');
    setPlacement('');
    setResult(null);
  };

  const templates = product ? TEMPLATES[product] : [];
  const placements = product ? PLACEMENTS[product] : [];
  const canGenerate = designImage && product && template && placement && !generating;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/30 bg-background/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Mockup Generator</h1>
              <p className="text-sm text-muted-foreground">Create Etsy listing photos</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          
          {/* Left: Controls (2 cols) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Upload */}
            <div className="premium-card p-6">
              <Label className="text-sm font-semibold text-violet-400 mb-4 block">STEP 1</Label>
              <h3 className="text-lg font-semibold mb-4">Upload Design</h3>
              <div
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all
                  ${designPreview 
                    ? 'border-violet-500/50 bg-violet-500/5' 
                    : 'border-border/50 hover:border-violet-500/30 hover:bg-violet-500/5'}`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  className="hidden"
                />
                {designPreview ? (
                  <div className="space-y-3">
                    <img src={designPreview} alt="Design" className="max-h-24 mx-auto rounded-lg object-contain" />
                    <p className="text-sm text-muted-foreground">Click to replace</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="w-14 h-14 bg-violet-500/10 rounded-2xl flex items-center justify-center mx-auto">
                      <Upload className="w-6 h-6 text-violet-400" />
                    </div>
                    <p className="font-medium">Drop your design</p>
                    <p className="text-sm text-muted-foreground">PNG with transparency</p>
                  </div>
                )}
              </div>
            </div>

            {/* Product */}
            <div className="premium-card p-6">
              <Label className="text-sm font-semibold text-violet-400 mb-4 block">STEP 2</Label>
              <h3 className="text-lg font-semibold mb-4">Select Product</h3>
              <div className="grid grid-cols-3 gap-2">
                {PRODUCTS.map((p) => {
                  const Icon = p.icon;
                  const selected = product === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => handleProductSelect(p.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-center
                        ${selected 
                          ? 'border-violet-500 bg-violet-500/10' 
                          : 'border-border/50 hover:border-violet-500/30'}`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-2 ${selected ? 'text-violet-400' : 'text-muted-foreground'}`} />
                      <span className="text-xs font-medium">{p.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Style */}
            {product && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="premium-card p-6"
              >
                <Label className="text-sm font-semibold text-violet-400 mb-4 block">STEP 3</Label>
                <h3 className="text-lg font-semibold mb-4">Choose Style</h3>
                <div className="grid grid-cols-3 gap-2">
                  {templates.map((t) => {
                    const selected = template === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => handleTemplateSelect(t.id)}
                        className={`rounded-xl border-2 transition-all overflow-hidden
                          ${selected 
                            ? 'border-violet-500 ring-2 ring-violet-500/20' 
                            : 'border-border/50 hover:border-violet-500/30'}`}
                      >
                        <div className="aspect-square">
                          <img src={t.file} alt={t.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-2">
                          <span className="text-xs font-medium">{t.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Placement */}
            {template && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="premium-card p-6"
              >
                <Label className="text-sm font-semibold text-violet-400 mb-4 block">STEP 4</Label>
                <h3 className="text-lg font-semibold mb-4">Placement</h3>
                <div className="flex flex-wrap gap-2">
                  {placements.map((p) => {
                    const selected = placement === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => handlePlacementSelect(p.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all
                          ${selected 
                            ? 'bg-violet-500 text-white' 
                            : 'bg-violet-500/10 text-violet-300 hover:bg-violet-500/20'}`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Generate */}
            <button 
              onClick={handleGenerate}
              disabled={!canGenerate}
              className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-2
                ${canGenerate 
                  ? 'btn-gradient' 
                  : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
            >
              {generating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Mockup
                </>
              )}
            </button>

            {(designImage || result) && (
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={handleReset}>
                Start Over
              </Button>
            )}
          </div>

          {/* Right: Preview (3 cols) */}
          <div className="lg:col-span-3">
            <div className="premium-card p-6 h-full min-h-[600px] flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Preview</h3>
                {result && (
                  <button 
                    onClick={handleDownload}
                    className="btn-gradient text-sm px-5 py-2.5 rounded-full flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                )}
              </div>
              
              <div className="flex-1 flex items-center justify-center">
                {result ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full"
                  >
                    <div className="rounded-2xl overflow-hidden glow-sm">
                      <img src={result} alt="Mockup" className="w-full h-auto" />
                    </div>
                    <div className="mt-6 flex justify-center gap-4">
                      <button 
                        onClick={handleDownload}
                        className="btn-gradient px-8 py-3 rounded-full flex items-center gap-2"
                      >
                        <Download className="w-5 h-5" />
                        Download Mockup
                      </button>
                      <Button variant="outline" onClick={handleReset} className="px-6">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        New Mockup
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <div className="text-center">
                    <div className="w-20 h-20 bg-violet-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                      <ImageIcon className="w-10 h-10 text-violet-400/50" />
                    </div>
                    <p className="text-xl font-medium mb-2">Your mockup will appear here</p>
                    <p className="text-muted-foreground">
                      Upload → Product → Style → Placement → Generate
                    </p>
                  </div>
                )}
              </div>
              
              {/* Hidden canvas */}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockupGenerator;
